import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Building2, ImageIcon, CalendarDays, Clock, MapPin, Share2,
  Plus, Trash2, Save, Eye, Wand2, Trophy, Star, Users, Globe,
  CheckCircle2, AlertCircle, ExternalLink, Loader2, X, PartyPopper, CreditCard
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ProfileImageUpload, resolveAssetUrl } from '../../components/contractor/ProfileImageUpload';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'overview',  label: 'Overview',       icon: Building2     },
  { id: 'stands',    label: 'Stands',          icon: ImageIcon     },
  { id: 'upcoming',  label: 'Upcoming Shows',  icon: CalendarDays  },
  { id: 'past',      label: 'Past Shows',      icon: Clock         },
  { id: 'company',   label: 'Company',         icon: Users         },
  { id: 'awards',    label: 'Awards & Media',  icon: Trophy        },
  { id: 'social',    label: 'Social',          icon: Share2        },
  { id: 'payment',   label: 'Ödeme Bilgileri', icon: CreditCard    },
];

const INDUSTRIES = [
  'Technology', 'Automotive', 'Healthcare', 'Food & Beverage', 'Fashion & Apparel',
  'Construction', 'Machinery', 'Energy', 'Consumer Goods', 'Aerospace',
  'Agriculture', 'Chemicals', 'Education', 'Finance', 'Furniture & Interiors',
  'Beauty & Cosmetics', 'Sports & Recreation', 'Travel & Tourism', 'Media',
];

const STAND_STYLES = ['Modular', 'Custom Build', 'Shell Scheme', 'Island', 'Peninsula', 'Inline', 'Double-decker', 'Outdoor'];
const SHOW_STATUS = { confirmed: 'bg-green-100 text-green-700', tentative: 'bg-yellow-100 text-yellow-700', cancelled: 'bg-red-100 text-red-700' };

const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1';
const inputCls = 'w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white';

const emptyStand = () => ({
  id: `std_${Math.random().toString(36).slice(2, 10)}`,
  title: '', show_name: '', venue: '', city: '', country: '', year: new Date().getFullYear(),
  sqm: null, booth_number: '', description: '', images: [], video_url: '', status: 'active',
});
const emptyShow = () => ({
  id: `ush_${Math.random().toString(36).slice(2, 10)}`,
  show_name: '', venue: '', city: '', country: '', start_date: '', end_date: '',
  hall: '', booth_number: '', description: '', image_url: null, ticket_url: '', status: 'confirmed',
});
const emptyPast = () => ({ id: `psh_${Math.random().toString(36).slice(2, 10)}`, show_name: '', city: '', country: '', year: '', sqm: '' });
const emptyAward = () => ({ id: `awd_${Math.random().toString(36).slice(2, 10)}`, title: '', organization: '', year: '', url: '' });

// ── Confetti Animation ────────────────────────────────────────────────────────
const CONFETTI_COLORS_E = ['#2563eb', '#f97316', '#16a34a', '#a855f7', '#ec4899', '#eab308', '#06b6d4'];
const ConfettiBlastE = ({ onDone }) => {
  const particles = useRef(
    Array.from({ length: 50 }, (_, i) => ({
      id: i,
      color: CONFETTI_COLORS_E[i % CONFETTI_COLORS_E.length],
      left: `${(i / 50) * 100 + (Math.random() - 0.5) * 8}%`,
      delay: `${(i / 50) * 0.6}s`,
      duration: `${1.0 + (i % 5) * 0.15}s`,
      size: `${6 + (i % 4) * 2}px`,
      shape: i % 3 === 0 ? '50%' : i % 3 === 1 ? '0' : '2px',
    }))
  ).current;
  useEffect(() => { const t = setTimeout(onDone, 2500); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute', top: '-20px', left: p.left,
          width: p.size, height: p.size, backgroundColor: p.color,
          borderRadius: p.shape, animationDelay: p.delay, animationDuration: p.duration,
          animation: `confettiFall ${p.duration} ${p.delay} ease-in forwards`,
        }} />
      ))}
      <style>{`@keyframes confettiFall { 0% { transform: translateY(0) rotate(0deg) scale(1); opacity: 1; } 100% { transform: translateY(105vh) rotate(720deg) scale(0.8); opacity: 0; } }`}</style>
    </div>
  );
};

export default function ExhibitorProfile() {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => location.state?.tab || 'overview');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [orgSlug, setOrgSlug] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const prevScoreRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');

  const scheduleAutoSave = React.useCallback((patch) => {
    setAutoSaveStatus('saving');
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      if (!token || !patch || !Object.keys(patch).length) return;
      try {
        await axios.patch(`${API}/api/exhibitor/profile`, patch, { headers: { Authorization: `Bearer ${token}` } });
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(''), 2500);
      } catch { setAutoSaveStatus(''); }
    }, 1500);
  }, []);

  // State per tab — must be declared before the auto-save useEffect that reads from overview
  const [overview, setOverview] = useState({ tagline: '', about: '', logo_url: null, cover_image_url: null });

  // Auto-save overview changes
  useEffect(() => {
    scheduleAutoSave({ tagline: overview.tagline, about: overview.about, logo_url: overview.logo_url, cover_image_url: overview.cover_image_url });
  // eslint-disable-next-line
  }, [overview.tagline, overview.about, overview.logo_url, overview.cover_image_url]);
  const [industries, setIndustries] = useState([]);
  const [stands, setStands] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [company, setCompany] = useState({ company_size: '', founded_year: '', headquarters_country: '', headquarters_city: '', preferred_stand_styles: [], typical_booth_size: '', annual_budget_range: '', stats: { shows_participated: 0, countries_exhibited: 0, years_exhibiting: 0, total_stand_sqm: 0 } });
  const [awards, setAwards] = useState([]);
  const [social, setSocial] = useState({ website: '', linkedin: '', instagram: '', twitter: '', youtube: '' });
  const [paymentInfo, setPaymentInfo] = useState({ bank_name: '', account_holder: '', iban: '', swift_bic: '', payment_notes: '' });

  // Load profile
  useEffect(() => {
    axios.get(`${API}/api/exhibitor/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        const p = data.profile || {};
        setOrgSlug(data.org?.slug || '');
        setOverview({ tagline: p.tagline || '', about: p.about || '', logo_url: p.logo_url || null, cover_image_url: p.cover_image_url || null });
        setIndustries(p.industries || []);
        setStands(p.stands || []);
        setUpcoming(p.upcoming_shows || []);
        setPast(p.past_shows || []);
        setCompany({
          company_size: p.company_size || '',
          founded_year: p.founded_year || '',
          headquarters_country: p.headquarters_country || '',
          headquarters_city: p.headquarters_city || '',
          preferred_stand_styles: p.preferred_stand_styles || [],
          typical_booth_size: p.typical_booth_size || '',
          annual_budget_range: p.annual_budget_range || '',
          stats: p.stats || { shows_participated: 0, countries_exhibited: 0, years_exhibiting: 0, total_stand_sqm: 0 },
        });
        setAwards(p.awards || []);
        setSocial(p.social || { website: '', linkedin: '', instagram: '', twitter: '', youtube: '' });
        setPaymentInfo(p.payment_info || { bank_name: '', account_holder: '', iban: '', swift_bic: '', payment_notes: '' });
      })
      .catch(() => setToast({ type: 'error', msg: 'Profile could not be loaded.' }))
      .finally(() => {
        setLoading(false);
        // Init prevScoreRef for confetti baseline
        axios.get(`${API}/api/exhibitor/profile-completion`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => { prevScoreRef.current = r.data?.score ?? null; }).catch(() => { prevScoreRef.current = 0; });
      });
  }, [token]);

  const handleSave = async () => {
    if (!overview.tagline?.trim() && !overview.about?.trim() && !company.name?.trim()) {
      setToast({ type: 'error', msg: t('validation.required') + ' — Şirket adı veya açıklama ekleyin' });
      setTimeout(() => setToast(null), 3500);
      return;
    }
    setSaving(true);
    const body = {
      tagline: overview.tagline, about: overview.about,
      logo_url: overview.logo_url, cover_image_url: overview.cover_image_url,
      industries, stands, upcoming_shows: upcoming, past_shows: past,
      ...company, stats: company.stats, awards, social,
      payment_info: (paymentInfo.bank_name || paymentInfo.iban) ? { ...paymentInfo } : null,
    };
    try {
      await axios.patch(`${API}/api/exhibitor/profile`, body, { headers: { Authorization: `Bearer ${token}` } });
      setToast({ type: 'success', msg: 'Profile saved successfully!' });
      // Check for 100% completion confetti
      try {
        const compRes = await axios.get(`${API}/api/exhibitor/profile-completion`, { headers: { Authorization: `Bearer ${token}` } });
        const newScore = compRes.data?.score;
        if (newScore === 100 && prevScoreRef.current !== null && prevScoreRef.current < 100) {
          setShowConfetti(true);
        }
        prevScoreRef.current = newScore;
      } catch {}
    } catch {
      setToast({ type: 'error', msg: 'Save failed. Please try again.' });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 4000);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  // Confetti element — rendered outside main return below

  // ── Fill Demo Data ──────────────────────────────────────────────────────────
  const handleFillDemo = () => {
    setOverview({
      tagline: 'Connecting industries. Inspiring innovation. One stand at a time.',
      about: 'GlobalTech Exhibitions is a leading player in the B2B technology and industrial exhibitions landscape. With a presence at over 30 international trade shows annually, we bring cutting-edge solutions directly to decision-makers across 25+ countries. Our exhibition program is a cornerstone of our global go-to-market strategy.',
      cover_image_url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2200&auto=format&fit=crop',
      logo_url: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?q=80&w=300&auto=format&fit=crop',
    });
    setIndustries(['Technology', 'Machinery', 'Energy', 'Automotive']);
    setStands([
      { id: 'std_demo1', title: 'Innovation Hub — CES 2025', show_name: 'CES 2025', venue: 'Las Vegas Convention Center', city: 'Las Vegas', country: 'USA', year: 2025, sqm: 120, booth_number: 'Tech West – Booth 4523', description: 'Our flagship 120m² island booth featured interactive AI product demos, a media lounge, and a co-creation zone for partner integrations. The stand won Best Tech Stand Award at CES 2025.', images: ['https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?q=80&w=900', 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?q=80&w=900', 'https://images.unsplash.com/photo-1540575861501-7cf05a4b125a?q=80&w=900'], video_url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', status: 'active' },
      { id: 'std_demo2', title: 'Industrial Zone — Hannover Messe 2024', show_name: 'Hannover Messe 2024', venue: 'Hannover Exhibition Centre', city: 'Hannover', country: 'Germany', year: 2024, sqm: 200, booth_number: 'Hall 6 – B24', description: 'A 200m² showcase of our industrial automation portfolio. The stand featured live machine demonstrations, a dedicated VIP meeting room, and digital twin visualization stations.', images: ['https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=900', 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?q=80&w=900'], video_url: '', status: 'active' },
    ]);
    setUpcoming([
      { id: 'ush_demo1', show_name: 'Hannover Messe 2025', venue: 'Hannover Exhibition Centre', city: 'Hannover', country: 'Germany', start_date: '2025-03-31', end_date: '2025-04-04', hall: 'Hall 6', booth_number: 'B18', description: 'We\'re returning to Hannover Messe with our largest ever footprint — 280m² featuring our new AI-powered automation suite, robotics demonstrations, and an exclusive partner roundtable on April 2nd.', image_url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=900', ticket_url: 'https://www.hannovermesse.de', status: 'confirmed' },
      { id: 'ush_demo2', show_name: 'GITEX Global 2025', venue: 'Dubai World Trade Centre', city: 'Dubai', country: 'UAE', start_date: '2025-10-13', end_date: '2025-10-17', hall: 'Za\'abeel Hall', booth_number: 'Z-B45', description: 'Making our GITEX debut with a dedicated Middle East product showcase. Stop by our 60m² booth to see live demos of our cloud infrastructure solutions.', image_url: 'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?q=80&w=900', ticket_url: 'https://www.gitex.com', status: 'confirmed' },
      { id: 'ush_demo3', show_name: 'Mobile World Congress 2025', venue: 'Fira Barcelona Gran Via', city: 'Barcelona', country: 'Spain', start_date: '2025-03-03', end_date: '2025-03-06', hall: 'Hall 7', booth_number: '7H40', description: 'Showcasing our connectivity solutions at MWC. Come explore our 5G industrial IoT demo zone and meet our senior engineers.', image_url: null, ticket_url: 'https://www.mwcbarcelona.com', status: 'tentative' },
    ]);
    setPast([
      { id: 'psh_demo1', show_name: 'CES 2025', city: 'Las Vegas', country: 'USA', year: 2025, sqm: 120 },
      { id: 'psh_demo2', show_name: 'Hannover Messe 2024', city: 'Hannover', country: 'Germany', year: 2024, sqm: 200 },
      { id: 'psh_demo3', show_name: 'GITEX Global 2024', city: 'Dubai', country: 'UAE', year: 2024, sqm: 60 },
      { id: 'psh_demo4', show_name: 'Embedded World 2024', city: 'Nuremberg', country: 'Germany', year: 2024, sqm: 80 },
      { id: 'psh_demo5', show_name: 'Mobile World Congress 2024', city: 'Barcelona', country: 'Spain', year: 2024, sqm: 50 },
    ]);
    setCompany({
      company_size: '501-1000',
      founded_year: 2003,
      headquarters_country: 'Germany',
      headquarters_city: 'Munich',
      preferred_stand_styles: ['Island', 'Custom Build', 'Double-decker'],
      typical_booth_size: '80-200 m²',
      annual_budget_range: '€200,000 – €500,000',
      stats: { shows_participated: 142, countries_exhibited: 28, years_exhibiting: 21, total_stand_sqm: 15400 },
    });
    setAwards([
      { id: 'awd_demo1', title: 'Best Technology Stand — CES 2025', organization: 'Consumer Technology Association', year: 2025, url: 'https://ces.tech' },
      { id: 'awd_demo2', title: 'Innovation Exhibitor Award — Hannover Messe 2023', organization: 'Deutsche Messe AG', year: 2023, url: 'https://hannovermesse.de' },
      { id: 'awd_demo3', title: 'Sustainability Excellence in Exhibition — 2022', organization: 'UFI — Global Exhibition Industry Alliance', year: 2022, url: 'https://ufi.org' },
    ]);
    setSocial({ website: 'https://globaltech-exhibitions.com', linkedin: 'https://linkedin.com/company/globaltech', instagram: 'https://instagram.com/globaltechexpo', twitter: 'https://twitter.com/globaltechexpo', youtube: 'https://youtube.com/@globaltech' });
    setToast({ type: 'success', msg: 'All tabs filled with demo data!' });
    setTimeout(() => setToast(null), 4000);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 pb-12">
      {showConfetti && <ConfettiBlastE onDone={() => setShowConfetti(false)} />}
      {showConfetti && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-amber-50 border border-blue-200 shadow-sm mb-4">
          <PartyPopper className="h-6 w-6 text-blue-600" />
          <div>
            <p className="font-bold text-blue-800">Profile 100% Complete!</p>
            <p className="text-xs text-blue-600 mt-0.5">Your company profile now has maximum visibility to contractors. More proposals incoming!</p>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pt-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exhibitor Profile</h1>
          <p className="text-sm text-gray-500 mt-0.5">Build your public showcase page</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" className="gap-2 border-dashed border-blue-300 text-blue-700 hover:bg-blue-50" onClick={handleFillDemo} data-testid="fill-demo-btn">
            <Wand2 className="h-4 w-4" /> Fill Demo Data
          </Button>
          {orgSlug && (
            <Button variant="outline" size="sm" className="gap-2 text-gray-600" onClick={() => navigate(`/exhibitor/${orgSlug}`)}>
              <Eye className="h-4 w-4" /> View Public Page
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
            <Save className="h-4 w-4" />{saving ? 'Saving...' : 'Save All Changes'}
          </Button>
          {autoSaveStatus === 'saved' && (
            <span className="text-xs text-green-600 font-medium flex items-center gap-1" data-testid="autosave-indicator">
              <CheckCircle2 className="h-3.5 w-3.5" /> Otomatik kaydedildi
            </span>
          )}
          {autoSaveStatus === 'saving' && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Kaydediliyor...
            </span>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`mb-4 flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {toast.msg}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-gray-50 rounded-xl p-1 flex gap-1 mb-6 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${activeTab === t.id ? 'bg-white text-blue-700 shadow-sm border border-blue-100' : 'text-gray-500 hover:text-gray-700'}`}>
            <t.icon className="h-4 w-4" />{t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left column */}
          <div className="space-y-5">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm">Company Logo</CardTitle></CardHeader>
              <CardContent className="flex flex-col items-center gap-3">
                <ProfileImageUpload value={overview.logo_url} onChange={p => setOverview(x => ({ ...x, logo_url: p }))} onDelete={() => setOverview(x => ({ ...x, logo_url: null }))} assetType="exhibitor-logo" entityId="logo" shape="circle" label="Upload Logo" />
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm">Industries / Sectors</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-3">
                  {industries.map(ind => (
                    <span key={ind} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full font-medium">
                      {ind}<button onClick={() => setIndustries(p => p.filter(x => x !== ind))} className="text-blue-400 hover:text-blue-700 ml-0.5"><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
                <select className={inputCls} onChange={e => { if (e.target.value && !industries.includes(e.target.value)) setIndustries(p => [...p, e.target.value]); e.target.value = ''; }}>
                  <option value="">+ Add industry…</option>
                  {INDUSTRIES.filter(i => !industries.includes(i)).map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </CardContent>
            </Card>
          </div>
          {/* Right column */}
          <div className="lg:col-span-2 space-y-5">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm">Cover Image</CardTitle></CardHeader>
              <CardContent>
                <ProfileImageUpload value={overview.cover_image_url} onChange={p => setOverview(x => ({ ...x, cover_image_url: p }))} onDelete={() => setOverview(x => ({ ...x, cover_image_url: null }))} assetType="exhibitor-cover" entityId="cover" shape="rect" label="Upload Cover Image" aspectClass="aspect-video" />
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm">Company Identity</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className={labelCls}>Tagline</label>
                  <input className={inputCls} placeholder="One sentence that defines your brand…" value={overview.tagline} onChange={e => setOverview(p => ({ ...p, tagline: e.target.value }))} data-testid="tagline-input" />
                </div>
                <div>
                  <label className={labelCls}>About Company</label>
                  <textarea className={`${inputCls} h-28 resize-none`} placeholder="Describe your company, what you exhibit, your markets…" value={overview.about} onChange={e => setOverview(p => ({ ...p, about: e.target.value }))} />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ── STANDS ── */}
      {activeTab === 'stands' && (
        <div className="space-y-5">
          <div className="flex justify-end">
            <Button onClick={() => setStands(p => [emptyStand(), ...p])} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
              <Plus className="h-4 w-4" />Add Stand
            </Button>
          </div>
          {stands.length === 0 && (
            <div className="text-center py-16 text-gray-400 border-2 border-dashed rounded-xl">
              <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p className="font-medium">No stands yet — showcase your exhibition presence</p>
            </div>
          )}
          {stands.map((stand, si) => (
            <Card key={stand.id} className="border-0 shadow-sm">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">{stand.title || `Stand ${si + 1}`}</span>
                  {stand.show_name && <span className="text-xs text-gray-400">@ {stand.show_name}</span>}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${stand.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{stand.status}</span>
                </div>
                <button onClick={() => setStands(p => p.filter((_, i) => i !== si))} className="p-1 rounded hover:bg-red-50 text-red-400"><Trash2 className="h-4 w-4" /></button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelCls}>Stand Title *</label>
                    <input className={inputCls} placeholder="Innovation Hub — CES 2025" value={stand.title} onChange={e => setStands(p => p.map((x, i) => i === si ? { ...x, title: e.target.value } : x))} />
                  </div>
                  <div>
                    <label className={labelCls}>Show / Fair Name</label>
                    <input className={inputCls} placeholder="Hannover Messe 2024" value={stand.show_name || ''} onChange={e => setStands(p => p.map((x, i) => i === si ? { ...x, show_name: e.target.value } : x))} />
                  </div>
                  <div>
                    <label className={labelCls}>Venue</label>
                    <input className={inputCls} placeholder="Hannover Exhibition Centre" value={stand.venue || ''} onChange={e => setStands(p => p.map((x, i) => i === si ? { ...x, venue: e.target.value } : x))} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={labelCls}>City</label>
                      <input className={inputCls} placeholder="Hannover" value={stand.city || ''} onChange={e => setStands(p => p.map((x, i) => i === si ? { ...x, city: e.target.value } : x))} />
                    </div>
                    <div>
                      <label className={labelCls}>Country</label>
                      <input className={inputCls} placeholder="Germany" value={stand.country || ''} onChange={e => setStands(p => p.map((x, i) => i === si ? { ...x, country: e.target.value } : x))} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className={labelCls}>Year</label>
                      <input className={inputCls} type="number" placeholder="2024" value={stand.year || ''} onChange={e => setStands(p => p.map((x, i) => i === si ? { ...x, year: parseInt(e.target.value) || '' } : x))} />
                    </div>
                    <div>
                      <label className={labelCls}>Size (m²)</label>
                      <input className={inputCls} type="number" placeholder="120" value={stand.sqm || ''} onChange={e => setStands(p => p.map((x, i) => i === si ? { ...x, sqm: parseInt(e.target.value) || '' } : x))} />
                    </div>
                    <div>
                      <label className={labelCls}>Booth No.</label>
                      <input className={inputCls} placeholder="Hall 6 – B24" value={stand.booth_number || ''} onChange={e => setStands(p => p.map((x, i) => i === si ? { ...x, booth_number: e.target.value } : x))} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Video URL (optional)</label>
                    <input className={inputCls} placeholder="https://youtube.com/watch?v=..." value={stand.video_url || ''} onChange={e => setStands(p => p.map((x, i) => i === si ? { ...x, video_url: e.target.value } : x))} />
                  </div>
                </div>
                <div className="mb-4">
                  <label className={labelCls}>Description</label>
                  <textarea className={`${inputCls} h-20 resize-none`} placeholder="What did you showcase? What was special about this stand?" value={stand.description || ''} onChange={e => setStands(p => p.map((x, i) => i === si ? { ...x, description: e.target.value } : x))} />
                </div>
                {/* Stand Images — max 8 */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelCls}>Stand Photos (max 8)</label>
                    <span className="text-xs text-gray-400">{stand.images.length}/8</span>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {stand.images.map((img, ii) => (
                      <div key={ii} className="relative aspect-video rounded-lg overflow-hidden border bg-gray-50">
                        <img src={resolveAssetUrl(img)} alt="" className="w-full h-full object-cover" onError={e => e.target.style.display = 'none'} />
                        <button onClick={() => setStands(p => p.map((x, i) => i === si ? { ...x, images: x.images.filter((_, j) => j !== ii) } : x))} className="absolute top-1 right-1 p-0.5 bg-black/60 rounded text-white hover:bg-red-600">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    {stand.images.length < 8 && (
                      <ProfileImageUpload
                        value={null}
                        onChange={(path) => setStands(p => p.map((x, i) => i === si && x.images.length < 8 ? { ...x, images: [...x.images, path] } : x))}
                        assetType="exhibitor-stand"
                        entityId={`${stand.id}_img${stand.images.length}`}
                        shape="rect"
                        label="Add Photo"
                        aspectClass="aspect-video"
                        className="min-h-[80px]"
                      />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── UPCOMING SHOWS (timeline) ── */}
      {activeTab === 'upcoming' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setUpcoming(p => [emptyShow(), ...p])} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
              <Plus className="h-4 w-4" />Add Show
            </Button>
          </div>
          {upcoming.length === 0 && (
            <div className="text-center py-16 text-gray-400 border-2 border-dashed rounded-xl">
              <CalendarDays className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p className="font-medium">No upcoming shows scheduled</p>
            </div>
          )}
          {upcoming.map((show, si) => (
            <Card key={show.id} className="border-0 shadow-sm">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">{show.show_name || `Show ${si + 1}`}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${SHOW_STATUS[show.status] || ''}`}>{show.status}</span>
                </div>
                <button onClick={() => setUpcoming(p => p.filter((_, i) => i !== si))} className="p-1 rounded hover:bg-red-50 text-red-400"><Trash2 className="h-4 w-4" /></button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={labelCls}>Show Name *</label>
                    <input className={inputCls} placeholder="Hannover Messe 2025" value={show.show_name} onChange={e => setUpcoming(p => p.map((x, i) => i === si ? { ...x, show_name: e.target.value } : x))} />
                  </div>
                  <div>
                    <label className={labelCls}>Venue</label>
                    <input className={inputCls} placeholder="Convention Center" value={show.venue || ''} onChange={e => setUpcoming(p => p.map((x, i) => i === si ? { ...x, venue: e.target.value } : x))} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={labelCls}>Start Date</label>
                      <input className={inputCls} type="date" value={show.start_date || ''} onChange={e => setUpcoming(p => p.map((x, i) => i === si ? { ...x, start_date: e.target.value } : x))} />
                    </div>
                    <div>
                      <label className={labelCls}>End Date</label>
                      <input className={inputCls} type="date" value={show.end_date || ''} onChange={e => setUpcoming(p => p.map((x, i) => i === si ? { ...x, end_date: e.target.value } : x))} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={labelCls}>City</label>
                      <input className={inputCls} placeholder="Hannover" value={show.city || ''} onChange={e => setUpcoming(p => p.map((x, i) => i === si ? { ...x, city: e.target.value } : x))} />
                    </div>
                    <div>
                      <label className={labelCls}>Country</label>
                      <input className={inputCls} placeholder="Germany" value={show.country || ''} onChange={e => setUpcoming(p => p.map((x, i) => i === si ? { ...x, country: e.target.value } : x))} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={labelCls}>Hall</label>
                      <input className={inputCls} placeholder="Hall 6" value={show.hall || ''} onChange={e => setUpcoming(p => p.map((x, i) => i === si ? { ...x, hall: e.target.value } : x))} />
                    </div>
                    <div>
                      <label className={labelCls}>Booth No.</label>
                      <input className={inputCls} placeholder="B18" value={show.booth_number || ''} onChange={e => setUpcoming(p => p.map((x, i) => i === si ? { ...x, booth_number: e.target.value } : x))} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Status</label>
                    <select className={inputCls} value={show.status} onChange={e => setUpcoming(p => p.map((x, i) => i === si ? { ...x, status: e.target.value } : x))}>
                      <option value="confirmed">Confirmed</option>
                      <option value="tentative">Tentative</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                <div className="mb-4">
                  <label className={labelCls}>Description / News Text</label>
                  <textarea className={`${inputCls} h-20 resize-none`} placeholder="Tell visitors what you'll be showcasing, what's new, any special events…" value={show.description || ''} onChange={e => setUpcoming(p => p.map((x, i) => i === si ? { ...x, description: e.target.value } : x))} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Show Image</label>
                    <ProfileImageUpload value={show.image_url} onChange={p => setUpcoming(prev => prev.map((x, i) => i === si ? { ...x, image_url: p } : x))} onDelete={() => setUpcoming(prev => prev.map((x, i) => i === si ? { ...x, image_url: null } : x))} assetType="exhibitor-show" entityId={show.id} shape="rect" label="Upload Image" aspectClass="aspect-video" />
                  </div>
                  <div>
                    <label className={labelCls}>Registration / Ticket URL</label>
                    <input className={inputCls} placeholder="https://..." value={show.ticket_url || ''} onChange={e => setUpcoming(p => p.map((x, i) => i === si ? { ...x, ticket_url: e.target.value } : x))} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── PAST SHOWS ── */}
      {activeTab === 'past' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setPast(p => [emptyPast(), ...p])} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
              <Plus className="h-4 w-4" />Add Past Show
            </Button>
          </div>
          {past.length === 0 && (
            <div className="text-center py-12 text-gray-400 border-2 border-dashed rounded-xl">
              <Clock className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p>No past shows recorded</p>
            </div>
          )}
          <div className="bg-white rounded-xl border divide-y">
            {past.map((p_, pi) => (
              <div key={p_.id} className="flex items-center gap-3 p-3">
                <input className={`${inputCls} flex-1`} placeholder="Show Name" value={p_.show_name} onChange={e => setPast(p => p.map((x, i) => i === pi ? { ...x, show_name: e.target.value } : x))} />
                <input className={`${inputCls} w-28`} placeholder="City" value={p_.city || ''} onChange={e => setPast(p => p.map((x, i) => i === pi ? { ...x, city: e.target.value } : x))} />
                <input className={`${inputCls} w-28`} placeholder="Country" value={p_.country || ''} onChange={e => setPast(p => p.map((x, i) => i === pi ? { ...x, country: e.target.value } : x))} />
                <input className={`${inputCls} w-20`} type="number" placeholder="Year" value={p_.year || ''} onChange={e => setPast(p => p.map((x, i) => i === pi ? { ...x, year: parseInt(e.target.value) || '' } : x))} />
                <input className={`${inputCls} w-20`} type="number" placeholder="m²" value={p_.sqm || ''} onChange={e => setPast(p => p.map((x, i) => i === pi ? { ...x, sqm: parseInt(e.target.value) || '' } : x))} />
                <button onClick={() => setPast(p => p.filter((_, i) => i !== pi))} className="text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── COMPANY ── */}
      {activeTab === 'company' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-sm">Company Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'headquarters_country', label: 'HQ Country', placeholder: 'Germany' },
                { key: 'headquarters_city',    label: 'HQ City',    placeholder: 'Munich' },
              ].map(f => (
                <div key={f.key}>
                  <label className={labelCls}>{f.label}</label>
                  <input className={inputCls} placeholder={f.placeholder} value={company[f.key]} onChange={e => setCompany(p => ({ ...p, [f.key]: e.target.value }))} />
                </div>
              ))}
              <div>
                <label className={labelCls}>Company Size</label>
                <select className={inputCls} value={company.company_size} onChange={e => setCompany(p => ({ ...p, company_size: e.target.value }))}>
                  <option value="">Select…</option>
                  {['1-10','11-50','51-200','201-500','501-1000','1000+'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Founded Year</label>
                <input className={inputCls} type="number" placeholder="2005" value={company.founded_year} onChange={e => setCompany(p => ({ ...p, founded_year: parseInt(e.target.value) || '' }))} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-sm">Exhibition Profile</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className={labelCls}>Typical Booth Size</label>
                <input className={inputCls} placeholder="e.g. 50–150 m²" value={company.typical_booth_size} onChange={e => setCompany(p => ({ ...p, typical_booth_size: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Annual Exhibition Budget Range</label>
                <input className={inputCls} placeholder="e.g. €100,000 – €300,000" value={company.annual_budget_range} onChange={e => setCompany(p => ({ ...p, annual_budget_range: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Preferred Stand Styles</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {company.preferred_stand_styles.map(s => (
                    <span key={s} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">
                      {s}<button onClick={() => setCompany(p => ({ ...p, preferred_stand_styles: p.preferred_stand_styles.filter(x => x !== s) }))}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
                <select className={inputCls} onChange={e => { if (e.target.value && !company.preferred_stand_styles.includes(e.target.value)) setCompany(p => ({ ...p, preferred_stand_styles: [...p.preferred_stand_styles, e.target.value] })); e.target.value = ''; }}>
                  <option value="">+ Add style…</option>
                  {STAND_STYLES.filter(s => !company.preferred_stand_styles.includes(s)).map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </CardContent>
          </Card>
          <Card className="md:col-span-2 border-0 shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-sm">Exhibition Stats</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: 'shows_participated',  label: 'Shows Participated' },
                  { key: 'countries_exhibited',  label: 'Countries Exhibited' },
                  { key: 'years_exhibiting',     label: 'Years Exhibiting'    },
                  { key: 'total_stand_sqm',      label: 'Total Stand m²'      },
                ].map(s => (
                  <div key={s.key} className="bg-blue-50 rounded-xl p-4 text-center">
                    <label className="text-xs text-blue-600 font-medium block mb-2">{s.label}</label>
                    <input type="number" className="w-full text-center text-2xl font-bold text-blue-800 bg-transparent border-none focus:outline-none" value={company.stats[s.key]} onChange={e => setCompany(p => ({ ...p, stats: { ...p.stats, [s.key]: parseInt(e.target.value) || 0 } }))} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── AWARDS & MEDIA ── */}
      {activeTab === 'awards' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setAwards(p => [emptyAward(), ...p])} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
              <Plus className="h-4 w-4" />Add Award
            </Button>
          </div>
          {awards.length === 0 && (
            <div className="text-center py-12 text-gray-400 border-2 border-dashed rounded-xl">
              <Trophy className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p>No awards or recognition added yet</p>
            </div>
          )}
          <div className="space-y-3">
            {awards.map((award, ai) => (
              <Card key={award.id} className="border-0 shadow-sm">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Award Title *</label>
                        <input className={inputCls} placeholder="Best Innovation Stand" value={award.title} onChange={e => setAwards(p => p.map((x, i) => i === ai ? { ...x, title: e.target.value } : x))} />
                      </div>
                      <div>
                        <label className={labelCls}>Awarding Organization</label>
                        <input className={inputCls} placeholder="UFI, Deutsche Messe AG…" value={award.organization || ''} onChange={e => setAwards(p => p.map((x, i) => i === ai ? { ...x, organization: e.target.value } : x))} />
                      </div>
                      <div>
                        <label className={labelCls}>Year</label>
                        <input className={inputCls} type="number" placeholder="2024" value={award.year || ''} onChange={e => setAwards(p => p.map((x, i) => i === ai ? { ...x, year: parseInt(e.target.value) || '' } : x))} />
                      </div>
                      <div>
                        <label className={labelCls}>URL (optional)</label>
                        <input className={inputCls} placeholder="https://..." value={award.url || ''} onChange={e => setAwards(p => p.map((x, i) => i === ai ? { ...x, url: e.target.value } : x))} />
                      </div>
                    </div>
                    <button onClick={() => setAwards(p => p.filter((_, i) => i !== ai))} className="mt-1 p-1 rounded hover:bg-red-50 text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ── SOCIAL ── */}
      {activeTab === 'social' && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-sm">Online Presence & Contact</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'website',   label: 'Website',   placeholder: 'https://example.com' },
              { key: 'linkedin',  label: 'LinkedIn',  placeholder: 'https://linkedin.com/company/...' },
              { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...' },
              { key: 'twitter',   label: 'Twitter / X', placeholder: 'https://twitter.com/...' },
              { key: 'youtube',   label: 'YouTube',   placeholder: 'https://youtube.com/@...' },
            ].map(f => (
              <div key={f.key}>
                <label className={labelCls}>{f.label}</label>
                <input className={inputCls} placeholder={f.placeholder} value={social[f.key] || ''} onChange={e => setSocial(p => ({ ...p, [f.key]: e.target.value }))} data-testid={`social-${f.key}`} />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ── PAYMENT ── */}
      {activeTab === 'payment' && (
        <Card className="border-0 shadow-sm max-w-2xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-blue-600" />Fatura Ödeme Bilgileri
            </CardTitle>
            <p className="text-xs text-gray-400 mt-1">Bu bilgiler tedarikçilerden aldığınız faturaların ödemelerinde kullanılır.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Banka Adı</label>
                <input className={inputCls} placeholder="Ziraat Bankası"
                  value={paymentInfo.bank_name}
                  onChange={e => setPaymentInfo(p => ({ ...p, bank_name: e.target.value }))}
                  data-testid="exh-payment-bank-name" />
              </div>
              <div>
                <label className={labelCls}>Hesap Sahibi</label>
                <input className={inputCls} placeholder="ACME Exhibitions A.Ş."
                  value={paymentInfo.account_holder}
                  onChange={e => setPaymentInfo(p => ({ ...p, account_holder: e.target.value }))}
                  data-testid="exh-payment-account-holder" />
              </div>
            </div>
            <div>
              <label className={labelCls}>IBAN</label>
              <input className={inputCls} placeholder="TR12 0001 0017 4531 0823 1045 39"
                value={paymentInfo.iban}
                onChange={e => setPaymentInfo(p => ({ ...p, iban: e.target.value.toUpperCase() }))}
                data-testid="exh-payment-iban" />
            </div>
            <div>
              <label className={labelCls}>SWIFT / BIC</label>
              <input className={inputCls} placeholder="TCZBTR2A"
                value={paymentInfo.swift_bic}
                onChange={e => setPaymentInfo(p => ({ ...p, swift_bic: e.target.value.toUpperCase() }))}
                data-testid="exh-payment-swift" />
            </div>
            <div>
              <label className={labelCls}>Ödeme Notu</label>
              <textarea className={inputCls + " resize-none"} rows={2}
                placeholder="Ödeme açıklamasına fatura numarasını yazınız."
                value={paymentInfo.payment_notes}
                onChange={e => setPaymentInfo(p => ({ ...p, payment_notes: e.target.value }))}
                data-testid="exh-payment-notes" />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}